import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, MessageSquare, Trash2, LogOut, X, Coins, Zap, History,
  MoreHorizontal, Pin, PinOff, Pencil, Shield,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LogoLockup } from "@/components/Logo";

export const Sidebar = ({ conversations, activeId, onSelect, onNew, onDelete, onRename, onPin, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [toDelete, setToDelete] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const openRename = (c) => {
    setRenameTarget(c);
    setRenameValue(c.title);
  };
  const saveRename = () => {
    if (renameTarget && renameValue.trim()) onRename(renameTarget.id, renameValue.trim());
    setRenameTarget(null);
  };

  return (
    <aside className="w-72 h-full bg-[#0A0A0A] border-r border-white/10 flex flex-col" data-testid="sidebar">
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LogoLockup />
        </div>
        <button onClick={onClose} className="md:hidden text-zinc-400 hover:text-white" data-testid="sidebar-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="px-3">
        <button
          data-testid="new-chat-btn"
          onClick={onNew}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#141415] border border-white/10 hover:border-[#00FF66]/40 hover:bg-[#1F1F22] text-white text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4 text-[#00FF66]" />
          New chat
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 mt-4 space-y-1" data-testid="conversation-list">
        {conversations.length === 0 && (
          <p className="text-xs text-zinc-600 px-3 py-2">No conversations yet.</p>
        )}
        {conversations.map((c) => (
          <div
            key={c.id}
            data-testid={`conversation-item-${c.id}`}
            onClick={() => onSelect(c.id)}
            className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
              activeId === c.id ? "bg-[#1F1F22] text-white" : "text-zinc-400 hover:bg-[#141415] hover:text-white"
            }`}
          >
            {c.pinned ? (
              <Pin className="w-3.5 h-3.5 shrink-0 text-[#00FF66] fill-[#00FF66]" />
            ) : (
              <MessageSquare className="w-4 h-4 shrink-0" />
            )}
            <span className="flex-1 truncate text-sm">{c.title}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <button
                  data-testid={`conversation-menu-${c.id}`}
                  className="opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 text-zinc-500 hover:text-white transition-opacity"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#141415] border-white/10 text-white min-w-[160px]">
                <DropdownMenuItem
                  data-testid={`rename-conversation-${c.id}`}
                  onClick={(e) => { e.stopPropagation(); openRename(c); }}
                  className="cursor-pointer focus:bg-[#1F1F22] focus:text-white gap-2"
                >
                  <Pencil className="w-4 h-4" /> Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid={`pin-conversation-${c.id}`}
                  onClick={(e) => { e.stopPropagation(); onPin(c.id, !c.pinned); }}
                  className="cursor-pointer focus:bg-[#1F1F22] focus:text-white gap-2"
                >
                  {c.pinned ? <><PinOff className="w-4 h-4" /> Unpin</> : <><Pin className="w-4 h-4" /> Pin to top</>}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid={`delete-conversation-${c.id}`}
                  onClick={(e) => { e.stopPropagation(); setToDelete(c); }}
                  className="cursor-pointer focus:bg-red-500/10 text-red-400 focus:text-red-400 gap-2"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>

      <div className="p-3 border-t border-white/10 space-y-2">
        <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-[#141415] border border-white/10">
          <div className="flex items-center gap-2">
            <Coins className="w-4 h-4 text-[#00FF66]" />
            <span className="text-sm text-white font-medium" data-testid="sidebar-credits">
              {user?.unlimited_credits ? "Unlimited" : `${(user?.credits ?? 0).toLocaleString()} credits`}
            </span>
          </div>
        </div>
        {!user?.unlimited_credits && (
          <button
            data-testid="sidebar-buy-credits"
            onClick={() => navigate("/billing")}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-[#00FF66] hover:bg-[#00CC52] text-black text-sm font-semibold transition-colors"
          >
            <Zap className="w-4 h-4" />
            Buy credits
          </button>
        )}
        <button
          data-testid="sidebar-usage"
          onClick={() => navigate("/usage")}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-zinc-400 hover:bg-[#141415] hover:text-white text-sm transition-colors"
        >
          <History className="w-4 h-4" />
          Usage & history
        </button>
        {user?.role === "admin" && (
          <button
            data-testid="sidebar-admin"
            onClick={() => navigate("/admin")}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-zinc-400 hover:bg-[#141415] hover:text-white text-sm transition-colors"
          >
            <Shield className="w-4 h-4" />
            Admin dashboard
          </button>
        )}
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-[#00FF66] flex items-center justify-center text-black font-semibold text-sm shrink-0">
            {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate flex items-center gap-1.5">
              {user?.name || "User"}
              {user?.role === "admin" && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00FF66]/20 text-[#00FF66] font-semibold">ADMIN</span>
              )}
            </p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="text-zinc-400 hover:text-red-400 transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="bg-[#141415] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Rename conversation</DialogTitle>
          </DialogHeader>
          <Input
            data-testid="rename-input"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveRename()}
            autoFocus
            className="bg-[#0A0A0A] border-white/10 text-white h-11 focus-visible:ring-[#00FF66]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)} className="bg-transparent border-white/10 text-white hover:bg-[#1F1F22]">
              Cancel
            </Button>
            <Button data-testid="rename-save-btn" onClick={saveRename} className="bg-[#00FF66] hover:bg-[#00CC52] text-black font-semibold">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#141415] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              "{toDelete?.title}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-[#1F1F22]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={() => { onDelete(toDelete.id); setToDelete(null); }}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
};
